import { type FormEvent, useState } from "react";
import i18next, { t } from "i18next";
import { Link, Navigate, createFileRoute } from "@tanstack/react-router";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MuiLink from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { authService, useAuth } from "@/lib/auth";

export const Route = createFileRoute("/forgot-password")({
	component: ForgotPasswordPage,
});

export function ForgotPasswordPage() {
	const { status, user } = useAuth();
	const [email, setEmail] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [success, setSuccess] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	if (status === "loading") return null;

	if (status === "authenticated" && user) {
		return <Navigate to="/dashboard" replace />;
	}

	if (success) {
		return (
			<Box
				sx={{
					minHeight: "100vh",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					bgcolor: "background.level1",
					p: 2,
				}}
			>
				<Paper
					sx={{
						width: { xs: "100%", sm: 420 },
						p: { xs: 3, sm: 4 },
						borderRadius: 2,
					}}
				>
					<Stack spacing={3}>
						<Stack spacing={0.5}>
							<Typography variant="h5">{t("Check your email")}</Typography>
							<Typography variant="body2" color="text.secondary">
								{t(
									"If that email is registered, you'll receive a reset link shortly. The link expires in 1 hour."
								)}
							</Typography>
						</Stack>
						<MuiLink
							component={Link as React.ElementType}
							to="/login"
							variant="body2"
							color="text.secondary"
						>
							{t("Back to login")}
						</MuiLink>
					</Stack>
				</Paper>
			</Box>
		);
	}

	const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setErrorMessage(null);
		setIsSubmitting(true);
		try {
			await authService.forgotPassword({
				email,
				language: i18next.language === "nl" ? "nl" : "en",
			});
			setSuccess(true);
		} catch (error) {
			const message = error instanceof Error ? error.message : "";
			if (message === "RATE_LIMITED") {
				setErrorMessage(t("Too many requests. Please try again later."));
			} else {
				setErrorMessage(t("Something went wrong. Please try again."));
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Box
			sx={{
				minHeight: "100vh",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				bgcolor: "background.level1",
				p: 2,
			}}
		>
			<Paper
				component="form"
				onSubmit={handleSubmit}
				sx={{
					width: { xs: "100%", sm: 420 },
					p: { xs: 3, sm: 4 },
					borderRadius: 2,
				}}
			>
				<Stack spacing={3}>
					<Stack spacing={0.5}>
						<Typography variant="h5">{t("Reset your password")}</Typography>
						<Typography variant="body2" color="text.secondary">
							{t(
								"Enter your email and we'll send you a link to choose a new password."
							)}
						</Typography>
					</Stack>
					<TextField
						label={t("Email")}
						type="email"
						autoComplete="email"
						value={email}
						onChange={(event) => setEmail(event.target.value)}
						required
						fullWidth
					/>
					{errorMessage && (
						<Typography variant="body2" color="error.main">
							{errorMessage}
						</Typography>
					)}
					<Button
						type="submit"
						variant="contained"
						size="large"
						disabled={isSubmitting}
					>
						{isSubmitting ? t("Sending…") : t("Send reset link")}
					</Button>
					<Box sx={{ textAlign: "center" }}>
						<MuiLink
							component={Link as React.ElementType}
							to="/login"
							variant="body2"
							color="text.secondary"
						>
							{t("Back to login")}
						</MuiLink>
					</Box>
				</Stack>
			</Paper>
		</Box>
	);
}
